import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import * as echarts from 'echarts';
import {
  siteDataApi,
  type TargetTypePeriod,
  type TargetTypeRow,
  type TargetTypePieRow,
  type TargetTypeParams,
} from '../api/siteData';
import { ReportExportButton } from '../components/ReportExportButton';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { MultiSelect } from '../components/MultiSelect';
import { getCurrentCalendarRange, MarketingDateRangePicker } from '../components/MarketingTimeRangeFilter';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { useSiteShopName } from '../hooks/useSiteShopName';
import { useLazySection } from '../hooks/useLazySection';
import { useAppStore } from '../store/useAppStore';
import { formatSiteMetric, isSiteSensitiveMetric } from '../lib/siteDataSecurity';
import { SortableHeader, useTableSort, type SortValue } from '../components/SortableTable';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';

const fmtNum = (v: number) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtPct = (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%');

function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function rangeFor(period: TargetTypePeriod): { start: string; end: string } {
  const t = new Date(); const y = t.getFullYear(); const m = t.getMonth();
  const iso = fmtLocalDate;
  if (period === 'day') {
    const end = new Date(t); end.setDate(end.getDate() - 1);
    const start = new Date(end); start.setDate(start.getDate() - 6);
    return { start: iso(start), end: iso(end) };
  }
  if (period === 'week') {
    const end = new Date(t); end.setDate(end.getDate() - 1);
    const start = new Date(end); start.setDate(start.getDate() - 27);
    return { start: iso(start), end: iso(end) };
  }
  if (period === 'month') return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };
  if (period === 'quarter') { const qs = Math.floor(m / 3) * 3; return { start: iso(new Date(y, qs, 1)), end: iso(new Date(y, qs + 3, 0)) }; }
  if (period === 'half_year') { const hs = m < 6 ? 0 : 6; return { start: iso(new Date(y, hs, 1)), end: iso(new Date(y, hs + 6, 0)) }; }
  return { start: iso(new Date(y, 0, 1)), end: iso(new Date(y, 11, 31)) }; // year
}

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

function daysAgo(n: number): string {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return fmtLocalDate(date);
}

function today(): string {
  return fmtLocalDate(new Date());
}

function getPresetRange(preset: Exclude<DatePreset, 'custom'>): { start: string; end: string } {
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'yesterday', label: '昨天' },
  { key: '7d', label: '近7天' },
  { key: '14d', label: '近14天' },
  { key: '30d', label: '近30天' },
  { key: 'thisWeek', label: '本周' },
  { key: 'thisMonth', label: '本月' },
  { key: 'custom', label: '自定义' },
];

const PERIODS: { key: TargetTypePeriod; label: string }[] = [
  { key: 'day', label: '日' }, { key: 'week', label: '周' }, { key: 'month', label: '月' },
  { key: 'quarter', label: '季' }, { key: 'half_year', label: '半年' }, { key: 'year', label: '年' },
];

const TARGET_TYPE_LABEL_FIELDS = ['platform', 'cost_type', 'new_old_type'] as const;

function TargetTypeFilter({ label, field, value, onChange }: {
  label: string;
  field: typeof TARGET_TYPE_LABEL_FIELDS[number];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="data-filter-field" style={{ minWidth: 180 }}>
      <span className="data-filter-label">{label}</span>
      <ReportLabelSelect
        dataset="target_type"
        field={field}
        pageFields={TARGET_TYPE_LABEL_FIELDS}
        includeAll
        single
        value={value ? [value] : ['全部']}
        onChange={next => onChange(next[0] === '全部' ? '' : (next[0] ?? ''))}
        width="100%"
      />
    </label>
  );
}

// ── echarts 饼图：每个平台的费用占比（标签显示占比%）─────────────────
function PieChart({ rows, height = 380 }: { rows: TargetTypePieRow[]; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!chartRef.current) chartRef.current = echarts.init(ref.current);
    const data = rows.filter(r => r.cost_rate > 0).map(r => ({ name: r.platform, value: r.cost_rate }));
    chartRef.current.setOption({
      tooltip: { trigger: 'item', formatter: (pp: any) => `${pp.name}<br/>占比 ${(pp.value * 100).toFixed(2)}%` },
      legend: { type: 'scroll', bottom: 0 },
      series: [{
        type: 'pie', radius: ['0%', '62%'], center: ['50%', '46%'],
        data,
        label: { show: true, formatter: (pp: any) => `${pp.name}: ${(pp.value * 100).toFixed(2)}%`, fontSize: 11 },
        labelLine: { show: true },
        labelLayout: { hideOverlap: false },
      }],
    }, true);
  }, [rows]);
  useEffect(() => { const f = () => chartRef.current?.resize(); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  useEffect(() => () => { chartRef.current?.dispose(); chartRef.current = null; }, []);
  return <div ref={ref} style={{ width: '100%', height }} />;
}

const tdNum: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const thBase: React.CSSProperties = { background: 'var(--surface2)', whiteSpace: 'nowrap' };

function Card({ title, children, extra }: { title: string; children: ReactNode; extra?: ReactNode }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
        {extra}
      </div>
      {children}
    </div>
  );
}

type TargetTypeDimensionKey = 'date' | 'target_type' | 'platform' | 'new_old';

const TARGET_TYPE_DIMENSIONS: { value: TargetTypeDimensionKey; label: string }[] = [
  { value: 'date', label: '日期' },
  { value: 'target_type', label: '目标类型' },
  { value: 'platform', label: '效果营销平台' },
  { value: 'new_old', label: '广告新老客' },
];

const TOTAL_ROW_LABELS = new Set(['总计', '合计', '小计']);

function isTargetTypeTotalRow(row: TargetTypeRow): boolean {
  return Object.values(row.dimensions ?? {}).some(value => TOTAL_ROW_LABELS.has(value.trim()));
}

function targetTypeSortValue(row: TargetTypeRow, key: string): SortValue {
  if (key === 'spend' || key === 'net_sales' || key === 'cost_rate') {
    return row[key];
  }
  return row.dimensions?.[key] ?? (key === 'date' ? row.period ?? '' : '');
}

function targetTypeTableExportColumns(dimensions: TargetTypeDimensionKey[]) {
  return [
    ...dimensions.map(key => ({ key, label: TARGET_TYPE_DIMENSIONS.find(option => option.value === key)?.label ?? key })),
  { key: 'spend', label: '账户花费' },
  { key: 'net_sales', label: '净销售额' },
  { key: 'cost_rate', label: '费用占比' },
  ];
}
const targetTypePieExportColumns = [
  { key: 'platform', label: '平台' },
  { key: 'cost_rate', label: '费用占比' },
];

export default function SiteTargetTypeLayered() {
  const { shopName: site } = useSiteShopName();
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const [period, setPeriod] = useState<TargetTypePeriod>('day');
  const init = rangeFor('day');
  const [startDate, setStartDate] = useState(init.start);
  const [endDate, setEndDate] = useState(init.end);
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [platform, setPlatform] = useState('');
  const [costType, setCostType] = useState('');
  const [newOldType, setNewOldType] = useState('');
  const [dimensions, setDimensions] = useState<TargetTypeDimensionKey[]>(['date']);

  const [table, setTable] = useState<TargetTypeRow[]>([]);
  const [effect, setEffect] = useState<TargetTypePieRow[]>([]);
  const [brand, setBrand] = useState<TargetTypePieRow[]>([]);
  const [newCust, setNewCust] = useState<TargetTypePieRow[]>([]);
  const [oldCust, setOldCust] = useState<TargetTypePieRow[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pieLoading, setPieLoading] = useState(false);
  const [error, setError] = useState('');
  const [queryVersion, setQueryVersion] = useState(0);
  const [tableExportSnapshot, setTableExportSnapshot] = useState<{ params: TargetTypeParams; dimensions: TargetTypeDimensionKey[] } | null>(null);
  const [pieExportParams, setPieExportParams] = useState<TargetTypeParams | null>(null);

  const totalRows = useMemo(() => table.filter(isTargetTypeTotalRow), [table]);
  const detailRows = useMemo(() => table.filter(row => !isTargetTypeTotalRow(row)), [table]);
  const { sort, toggleSort, sortedRows, setSort } = useTableSort<TargetTypeRow, string>(detailRows, targetTypeSortValue);
  const sortedTable = useMemo(() => [...totalRows, ...sortedRows], [totalRows, sortedRows]);

  useEffect(() => {
    if (sort && userLevel < 2 && isSiteSensitiveMetric(sort.key)) setSort(null);
  }, [sort, userLevel, setSort]);

  const onPeriod = (p: TargetTypePeriod) => {
    setPeriod(p);
    const r = datePreset === 'custom' ? rangeFor(p) : getPresetRange(datePreset);
    setStartDate(r.start); setEndDate(r.end);
  };

  const onDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset === 'custom') return;
    const r = getPresetRange(preset);
    setStartDate(r.start);
    setEndDate(r.end);
  };

  const baseParams = useMemo((): TargetTypeParams => ({
    site, start_date: startDate, end_date: endDate, period,
    platform, cost_type: costType, new_old_type: newOldType,
    dimensions: dimensions.join(','),
  }), [site, startDate, endDate, period, platform, costType, newOldType, dimensions]);

  const pieSection = useLazySection(Boolean(site));

  const fetchTable = useCallback(async () => {
    if (!site) return;
    setTableLoading(true); setError('');
    try {
      const tb = await siteDataApi.getTargetTypeTable(baseParams);
      setTable(tb.data?.list ?? []);
      setTableExportSnapshot({ params: { ...baseParams }, dimensions: [...dimensions] });
    } catch (e) {
      setTableExportSnapshot(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setTableLoading(false);
    }
  }, [site, baseParams]);

  const fetchPies = useCallback(async () => {
    if (!site) return;
    setPieLoading(true); setError('');
    try {
      const [ef, br, nw, od] = await Promise.all([
        siteDataApi.getTargetTypePie({ ...baseParams, kind: 'effect' }),
        siteDataApi.getTargetTypePie({ ...baseParams, kind: 'brand' }),
        siteDataApi.getTargetTypePie({ ...baseParams, kind: 'new' }),
        siteDataApi.getTargetTypePie({ ...baseParams, kind: 'old' }),
      ]);
      setEffect(ef.data?.list ?? []);
      setBrand(br.data?.list ?? []);
      setNewCust(nw.data?.list ?? []);
      setOldCust(od.data?.list ?? []);
      setPieExportParams({ ...baseParams });
    } catch (e) {
      setPieExportParams(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setPieLoading(false);
    }
  }, [site, baseParams, dimensions]);

  const query = () => setQueryVersion(version => version + 1);
  useMarketingViewSection('page', {
    label: '页面筛选', state: { period, startDate, endDate, datePreset, platform, costType, newOldType, dimensions },
    restore: value => { setPeriod(value.period); setStartDate(value.startDate); setEndDate(value.endDate); setDatePreset(value.datePreset); setPlatform(value.platform); setCostType(value.costType); setNewOldType(value.newOldType); setDimensions(value.dimensions); },
    loading: tableLoading || pieLoading, query: () => { query(); },
    resolve: value => { if (value.datePreset === 'custom') return value; const range = getPresetRange(value.datePreset); return { ...value, datePreset: value.datePreset === 'today' ? 'custom' as const : value.datePreset, startDate: range.start, endDate: range.end }; },
    summary: startDate + ' ～ ' + endDate,
  });

  const reset = () => {
    setPeriod('day');
    setDatePreset('7d');
    const range = getPresetRange('7d');
    setStartDate(range.start);
    setEndDate(range.end);
    setPlatform('');
    setCostType('');
    setNewOldType('');
    setDimensions(['date']);
    setQueryVersion(version => version + 1);
  };
  useEffect(() => {
    if (site) void fetchTable();
  }, [queryVersion, site]);
  useEffect(() => {
    if (pieSection.ready) void fetchPies();
  }, [pieSection.ready, queryVersion]);

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      {/* 筛选 */}
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <span className="data-filter-inline-label">日期维度</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
          {PERIODS.map(t => (
            <div key={t.key} className={`page-tab${period === t.key ? ' active' : ''}`}
              onClick={() => onPeriod(t.key)}>{t.label}</div>
          ))}
          </div>

          <span className="data-filter-inline-label">日期范围</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {DATE_PRESETS.map(preset => (
              <div
                key={preset.key}
                className={`page-tab${datePreset === preset.key ? ' active' : ''}`}
                onClick={() => onDatePreset(preset.key)}
              >
                {preset.label}
              </div>
            ))}
          </div>

          {datePreset === 'custom' && <MarketingDateRangePicker start={startDate} end={endDate} max={today()} grain={period} onChange={({ start, end }) => { setStartDate(start); setEndDate(end); }} />}
          <DashboardQueryActions loading={tableLoading || pieLoading} onReset={reset} onQuery={query} />
        </div>
      </div>

      <div className="data-filter-card">
        <div className="data-filter-row compact">
          <TargetTypeFilter label="效果营销平台" field="platform" value={platform} onChange={setPlatform} />
          <TargetTypeFilter label="目标类型" field="cost_type" value={costType} onChange={setCostType} />
          <TargetTypeFilter label="广告新老客" field="new_old_type" value={newOldType} onChange={setNewOldType} />
        </div>
      </div>
      </div>

      {error && <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>{error}</div>}

      {/* 主表 */}
      <Card
        title="花费目标分层看板"
        extra={<ReportExportButton reportKey="site_target_type_table" title="花费目标分层看板" fileName={`花费目标分层看板_${tableExportSnapshot?.params.start_date ?? ''}_${tableExportSnapshot?.params.end_date ?? ''}.csv`} params={tableExportSnapshot?.params ?? {}} columns={targetTypeTableExportColumns(tableExportSnapshot?.dimensions ?? [])} disabled={!tableExportSnapshot} />}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)' }}>
          <div className="dimension-select-row" style={{ display: 'flex' }}>
            <span className="dimension-select-label">动态维度</span>
            <MultiSelect
              options={TARGET_TYPE_DIMENSIONS}
              value={dimensions}
              onChange={values => {
                const next = values.filter((value): value is TargetTypeDimensionKey => TARGET_TYPE_DIMENSIONS.some(option => option.value === value));
                setDimensions(next.length > 0 ? next : ['date']);
                setQueryVersion(version => version + 1);
              }}
              placeholder="请选择动态维度"
              countLabelFormatter={count => `已选 ${count} 项`}
              tagDisplay
              style={{ width: 420 }}
            />
          </div>
        </div>
        <div className="table-wrap" style={{ position: 'relative', maxHeight: 460, overflow: 'auto' }}>
          <table style={{ minWidth: Math.max(600, dimensions.length * 180 + 480) }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                {dimensions.map(key => (
                  <SortableHeader key={key} sortKey={key} sort={sort} onSort={toggleSort} style={thBase}>
                    {TARGET_TYPE_DIMENSIONS.find(option => option.value === key)?.label}
                  </SortableHeader>
                ))}
                <SortableHeader sortKey="spend" sort={sort} onSort={toggleSort} align="right" style={{ ...thBase, ...tdNum }} disabled={userLevel < 2 && isSiteSensitiveMetric('spend')}><MetricDescriptionTooltip label="账户花费" /></SortableHeader>
                <SortableHeader sortKey="net_sales" sort={sort} onSort={toggleSort} align="right" style={{ ...thBase, ...tdNum }} disabled={userLevel < 2 && isSiteSensitiveMetric('net_sales')}><MetricDescriptionTooltip label="净销售额" /></SortableHeader>
                <SortableHeader sortKey="cost_rate" sort={sort} onSort={toggleSort} align="right" style={{ ...thBase, ...tdNum }} disabled={userLevel < 2 && isSiteSensitiveMetric('cost_rate')}><MetricDescriptionTooltip label="费用占比" /></SortableHeader>
              </tr>
            </thead>
            <tbody>
              {tableLoading && table.length === 0 ? <tr><td colSpan={dimensions.length + 3}><TableLoadingCell /></td></tr>
                : table.length === 0 ? <tr><td colSpan={dimensions.length + 3} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
                : sortedTable.map((r, i) => (
                  <tr key={i}>
                    {dimensions.map(key => <td key={key} style={{ whiteSpace: 'nowrap' }}>{r.dimensions?.[key] ?? (key === 'date' ? r.period : '-')}</td>)}
                    <td style={tdNum}>{formatSiteMetric('spend', r.spend, fmtNum, userLevel)}</td>
                    <td style={tdNum}>{formatSiteMetric('net_sales', r.net_sales, fmtNum, userLevel)}</td>
                    <td style={tdNum}>{formatSiteMetric('cost_rate', r.cost_rate, fmtPct, userLevel)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {tableLoading && table.length > 0 && <TableLoadingOverlay />}
        </div>
      </Card>

      {/* 4 个饼图 2×2 */}
      <div ref={pieSection.ref} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
        <Card
          title="效广营销占比（分平台展示占比）"
          extra={<ReportExportButton reportKey="site_target_type_pie" title="效广营销占比" fileName={`效广营销占比_${pieExportParams?.start_date ?? ''}_${pieExportParams?.end_date ?? ''}.csv`} params={{ ...pieExportParams, kind: 'effect' }} columns={targetTypePieExportColumns} disabled={!pieExportParams} />}
        >
          <div style={{ position: 'relative' }}>
            {effect.length === 0 ? (pieLoading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>暂无数据</div>) : <PieChart rows={effect} />}
            {pieLoading && effect.length > 0 && <TableLoadingOverlay />}
          </div>
        </Card>
        <Card
          title="品牌广告营销占比（分平台展示占比）"
          extra={<ReportExportButton reportKey="site_target_type_pie" title="品牌广告营销占比" fileName={`品牌广告营销占比_${pieExportParams?.start_date ?? ''}_${pieExportParams?.end_date ?? ''}.csv`} params={{ ...pieExportParams, kind: 'brand' }} columns={targetTypePieExportColumns} disabled={!pieExportParams} />}
        >
          <div style={{ position: 'relative' }}>
            {brand.length === 0 ? (pieLoading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>暂无数据</div>) : <PieChart rows={brand} />}
            {pieLoading && brand.length > 0 && <TableLoadingOverlay />}
          </div>
        </Card>
        <Card
          title="新客营销占比（分平台展示占比）"
          extra={<ReportExportButton reportKey="site_target_type_pie" title="新客营销占比" fileName={`新客营销占比_${pieExportParams?.start_date ?? ''}_${pieExportParams?.end_date ?? ''}.csv`} params={{ ...pieExportParams, kind: 'new' }} columns={targetTypePieExportColumns} disabled={!pieExportParams} />}
        >
          <div style={{ position: 'relative' }}>
            {newCust.length === 0 ? (pieLoading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>暂无数据</div>) : <PieChart rows={newCust} />}
            {pieLoading && newCust.length > 0 && <TableLoadingOverlay />}
          </div>
        </Card>
        <Card
          title="老客营销占比（分平台展示占比）"
          extra={<ReportExportButton reportKey="site_target_type_pie" title="老客营销占比" fileName={`老客营销占比_${pieExportParams?.start_date ?? ''}_${pieExportParams?.end_date ?? ''}.csv`} params={{ ...pieExportParams, kind: 'old' }} columns={targetTypePieExportColumns} disabled={!pieExportParams} />}
        >
          <div style={{ position: 'relative' }}>
            {oldCust.length === 0 ? (pieLoading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>暂无数据</div>) : <PieChart rows={oldCust} />}
            {pieLoading && oldCust.length > 0 && <TableLoadingOverlay />}
          </div>
        </Card>
      </div>
    </div>
  );
}
